import { makeArray, isSubset } from './Util';
import { DAG } from './DAG';
import { NodeSet } from './NodeSet';
import { Component } from './Component';
import { identity } from './Functions';
import { Layout } from './Layout';

const _ = require('lodash');

/**
 * 
 */
export class Model {

  /**
   * Initialize a new empty Model
   */
  constructor(label) {
    this.label = label;

    /** @type {Component[]} */
    this.components = [];
    this.dag = new DAG();
    this.nodes = new NodeSet();
    this.layout = new Layout();
  }


  /**
   * Create a new component using other components as inputs
   * 
   * @param {String} newComponentLabel Label of the new component
   * @param {String[]} inputComponentLabels Array of component labels to use as inputs
   */
  addComponent(newComponentLabel, inputComponentLabels = []) {
    if (this.getComponent(newComponentLabel)) {
      throw Error('Component already exists. Skipping new component...');
    }
    
    // Create the new Component
    let newComponent = new Component(newComponentLabel || '');

    // Add to the model inventory
    this.components.push(newComponent);
    
    // Add the DAG links
    let inputComponentArray = makeArray(inputComponentLabels);

    for (let inputComponent of inputComponentArray) {
      this.dag.addLink(inputComponent, newComponentLabel);
    }

    return newComponent;
  }


  /**
   * Add a new component with inputs and an aggregation method (convenience function)
   * 
   * @param {String} newComponentLabel Label of the new component
   * @param {Function} method Method to apply on the inputs for this components data
   * @param  {...any} parameters Arbitrary number of parameters matching the method
   */
  addComponentWithMethod(newComponentLabel, method, parameterMapping) {
    let inputComponentLabels = Object.values(parameterMapping).flat().filter(v => typeof v === 'string');

    let newComponent = this.addComponent(newComponentLabel, inputComponentLabels);
    newComponent.updateMethod(method, parameterMapping);

    return newComponent;
  }


  /**
   * Create a new input-less component as the child of another component
   * 
   * @param {String} newComponentLabel Name of the new child component
   * @param {String} parentComponentLabel Name of the SINGLE parent component
   */
  addChildComponent(newComponentLabel, parentComponentLabel) {
    let newComponent = this.addComponent(newComponentLabel);

    this.dag.addLink(newComponentLabel, parentComponentLabel);

    return newComponent;
  }


  /**
   * Add a new adjustment component to an existing component and reroute the DAG
   * 
   * @param {String} componentLabel 
   * @param {Function} method Method to apply to the component
   * @param  {...any} parameters Arbitrary number of parameters matching the method
   */
  addComponentAdjustment(componentLabel, newLabel) {
    let method = identity;
    let parameterMapping = {'a': componentLabel};
    
    let oldOutputs = [...this.dag.getOutputs(componentLabel)]; // Ensure shallow copy

    let c = this.addComponentWithMethod(newLabel, method, parameterMapping);

    for (let output of oldOutputs) {
      // Update the DAG and Component attributes with the name swap
      this.dag.removeLink(componentLabel, output);
      this.dag.addLink(newLabel, output);

      this.getComponent(output).swapComponentInput(componentLabel, newLabel);
    }

    return c;
  }


  /**
   * Return the actual component(s) as referenced by their component labels
   * 
   * @param {String|String[]} labels Single component label or a list of component labels
   */
  getComponent(labels) {
    let labelList = makeArray(labels);
    let outputComponents = [];

    for (let label of labelList) {
      outputComponents.push(this.components.find(c => c.label === label));
    }

    return outputComponents.length === 1 ? outputComponents[0] : outputComponents;
  }


  /**
   * Add other components as inputs to the provided component
   * 
   * @param {String} componentLabel Label for the component whose inputs we are modifying
   * @param {String|String[]} addLabels Single component label or a list of component labels
   */
  addInputsToComponent(componentLabel, addLabels) {
    let newLabelsList = makeArray(addLabels);

    for (let label of newLabelsList) {
      this.dag.addLink(label, componentLabel);
    }
  }


  /**
   * Remove components as inputs to the provided component
   * 
   * @param {String} componentLabel Label for the component whose inputs we are modifying
   * @param {String|String[]} deleteLabels Single component label or a list of component labels
   */
  removeInputsToComponent(componentLabel, deleteLabels) {
    let deleteLabelList = makeArray(deleteLabels);
    
    for (let label of deleteLabelList) {
      this.dag.removeLink(label, componentLabel);
    }
  } 


  /**
   * Load remote data associated with any of the model components
   */
  async loadRemoteData() {
    let loads = [];
    for (let component of this.components) {
      loads.push(component.loadData());
    }
    return Promise.all(loads).then(() => {
      this.recalculate();
    });
  }


  /**
   * Recalculate the entire graph data to reflect the latest settings
   */
  recalculate() { // TODO: Enable a subgraph recalculate
    var calculatedNodeLabels = [];
    var nextToCalculateNodeLabels = this.dag.getLeafNodes();

    while (nextToCalculateNodeLabels.length > 0) {
      let nodeLabel = nextToCalculateNodeLabels.shift();
      let inputLabels = this.dag.getInputs(nodeLabel);

      if (isSubset(inputLabels, calculatedNodeLabels)) {
        // Grab the inputs and recalculate this node
        let inputComponents = inputLabels.map(input => this.getComponent(input));
        this.getComponent(nodeLabel).recalculate(inputComponents);

        // Identify any nodes that need to receive the new output
        let outputs = this.dag.getOutputs(nodeLabel);
        let newOutputs = _.difference(outputs, nextToCalculateNodeLabels);

        // Update the tracking queues
        nextToCalculateNodeLabels.push(...newOutputs);
        calculatedNodeLabels.push(nodeLabel);
      } else {
        // Since inputs are not ready, add back to the end of the calculation queue
        nextToCalculateNodeLabels.push(nodeLabel);
      }
    }
  }


  /**
   * Create a display DAG to pass into D3 using the raw calculation DAG
   * and the nodes combining nodes togethers
   */
  getDisplayDAG() {
    let output = [];
    for (let source in this.dag.directedDAG) {
      let outputSource = this.nodes.getNodeFromComponent(source);
      for (let dest of this.dag.directedDAG[source]) {
        let outputDest = this.nodes.getNodeFromComponent(dest);

        if (outputSource !== outputDest) {
          output.push([outputSource, outputDest]);
        }
      }
    }
    let dedupedOutput = _.uniqBy(output, (p) => JSON.stringify(p));
    return dedupedOutput;
  }

  /**
   * Return the component labels associated with the node label
   * 
   * @param {String} nodeLabel Label for the desired node
   */
  getDisplayComponents(label) {
    let components = this.nodes.getComponentsFromNode(label);
    if (components.length === 0) {
      return label;
    } else {
      return components;
    }
  }
}